import BACKEND_URL from 'constants/backendUrl';
import React, { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HttpError, POST } from 'utils/http';
import { toast } from 'react-toastify';
import { PasswordInput } from '../lib';
import Button from '../../Button';
import { REGEXP } from 'constants/validationsForm';
import { Link, useHistory } from 'react-router-dom';
import { SFormError } from 'components/Forms/SignUpForm/styles';
import { RESET_PASSWORD_PATH } from 'components/Forms/LoginForm';
import CheckboxInput from './CheckboxInput';

type Inputs = {
    newPassword: string;
    confirmNewPassword: string;
};

type NewPasswordFormProps = {
    id: number;
    token: string;
    hasCGUCheckbox: boolean;
};

const NewPasswordFetcher = async (id: number, token: string, password: string) =>
    POST(`${BACKEND_URL}/api/users/new-password`, {
        id,
        password,
        token,
    });

const validatePasswordConfirmation = (
    password: string | undefined,
    confirmation: string | undefined,
    message: string,
): boolean | string => (!!confirmation && password === confirmation ? true : message);

/**
 * Un lien expiré ou déjà utilisé n'est pas une panne : on dit pourquoi il ne marche plus, et où en
 * obtenir un nouveau.
 */
type LinkProblem = 'expired' | 'invalid';

const linkProblemFor = (error: unknown): LinkProblem | null => {
    if (!(error instanceof HttpError)) {
        return null;
    }
    if (410 === error.status) {
        return 'expired';
    }

    return 400 === error.status && 'This password link is not valid' === error.message ? 'invalid' : null;
};

const NewPasswordForm: FC<NewPasswordFormProps> = ({ id, token, hasCGUCheckbox }) => {
    const { t } = useTranslation();
    const methods = useForm<Inputs>();
    const history = useHistory();
    const [linkProblem, setLinkProblem] = useState<LinkProblem | null>(null);

    const { handleSubmit, getValues } = methods;

    const onSubmit = handleSubmit(async (values: Inputs) => {
        const { newPassword } = values;
        try {
            await NewPasswordFetcher(id, token, newPassword);
            toast.success(t('new-password-form.success'));
            history.push('/login');
        } catch (error) {
            const problem = linkProblemFor(error);
            if (problem) {
                setLinkProblem(problem);
            } else {
                toast.error(t('new-password-form.error'));
            }
        }
    });

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
                <PasswordInput
                    hint={t('common.password-rules')}
                    label={t('new-password-form.new-password.label')}
                    name="newPassword"
                    pattern={{ message: t(REGEXP.PASSWORD.message), value: REGEXP.PASSWORD.value }}
                    placeholder={t('new-password-form.new-password.placeholder')}
                    required
                />
                <PasswordInput
                    label={t('new-password-form.confirm-new-password.label')}
                    name="confirmNewPassword"
                    placeholder={t('new-password-form.confirm-new-password.placeholder')}
                    validate={(value) =>
                        validatePasswordConfirmation(
                            getValues('newPassword'),
                            value,
                            t('common.wrong-password-confirmation'),
                        )
                    }
                    required
                />
                {hasCGUCheckbox && <CheckboxInput />}
                {linkProblem && (
                    <SFormError role="alert">
                        {t(`new-password-form.link-${linkProblem}`)}{' '}
                        <Link to={RESET_PASSWORD_PATH}>{t('new-password-form.ask-for-a-new-link')}</Link>
                    </SFormError>
                )}
                <Button large submit>
                    {t('new-password-form.submit')}
                </Button>
            </form>
        </FormProvider>
    );
};

export default NewPasswordForm;
